import React, { useEffect, useState, useCallback } from "react";
import { RefreshControl } from "react-native";

import { ClaimStatus } from "libs/types/API";
import ClaimProcessing from "@/components/ClaimProcessing";
import ClaimProcessingProvider from "@/components/ClaimProcessing/ClaimProcessingProvider";
import { useRecoilValue, useRecoilState } from "recoil";
import { userDetails, refreshClaimsRequired as recoilRefreshClaimsRequired } from "@/utils/recoil/index";
import { FIND_CLAIMS } from "@/models/Claim";
import { organisationIdFromPropsAndUser } from "./helpers";
import { RouteKeys } from "@/navigationv2";
import { CCGApprovedPaidClaimsRoutes, CCGTabRoutes, ScreenNavigationProps, ScreenOptions } from "@/navigationv2/types";
import { createScreenTitle } from "@/navigationv2/utilities";
import { useOnMount, useOnScreenFocus } from "@/hooks";
import { redirectUserAfterLogin } from "@/screens/utils";
import { AppScreen } from "@/layouts/AppScreen";

interface PropTypes
  extends ScreenNavigationProps<CCGTabRoutes, CCGApprovedPaidClaimsRoutes, RouteKeys.ApprovedPaidClaimsScreen> {}

const ApprovedPaidClaimsScreen = (props: PropTypes) => {
  const user = useRecoilValue(userDetails);
  const { route } = props;

  const [refreshClaimsRequired, setRefreshClaimsRequired] = useRecoilState(recoilRefreshClaimsRequired);

  const [isUnmounted, setIsUnmounted] = useState(false);
  useEffect(() => () => setIsUnmounted(true), []);
  const onRefresh = useCallback(() => !isUnmounted && setRefreshClaimsRequired(true), [isUnmounted]);

  const organisationId = organisationIdFromPropsAndUser(route, user);

  const selectedQuery = FIND_CLAIMS;

  /**
   * If the user has tried to access a previous url but was unauthenticated,
   * they are dumped to the login screen where `previousUrl` is set. Then on
   * successful log in, this is passed to the screen where a hard redirect
   * is made to the previous route.
   */
  useOnMount(() => {
    redirectUserAfterLogin();
    return () => setIsUnmounted(true);
  });

  useOnScreenFocus(() => {
    setRefreshClaimsRequired(true);
  });

  return (
    <>
      <AppScreen
        contextLabel="Claims"
        pageTitle="Paid"
        refreshControl={<RefreshControl refreshing={refreshClaimsRequired} onRefresh={onRefresh} />}
        testID="CCG__ApprovedPaidClaims__Screen"
      >
        <ClaimProcessingProvider>
          <ClaimProcessing
            orgId={organisationId}
            selectedQuery={selectedQuery}
            claimsScreen={RouteKeys.ApprovedPaidClaimsScreen}
            type={ClaimStatus.approved_and_paid}
            refreshRequired={refreshClaimsRequired}
            onRefreshComplete={setRefreshClaimsRequired}
          />
        </ClaimProcessingProvider>
      </AppScreen>
    </>
  );
};

const screenOptions: ScreenOptions = {
  title: createScreenTitle("Approved Claims"),
};

ApprovedPaidClaimsScreen.screenOptions = screenOptions;

export default ApprovedPaidClaimsScreen;
